import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { persistReducer } from 'redux-persist';

import {
	IPayloadOAuthLoginRequest,
	IPayloadOAuthLoginResponse,
	RootState,
} from '..';
import { storage } from '../store.persist';
import {
	IPayloadDeviceLoginRequest,
	IPayloadLoginRequest,
	IPayloadLoginSuccess,
	IPayloadLogoutRequest,
	IPayloadRenewToken,
	IPayloadVerifyOtpRequest,
} from './auth.type';

const initialState = {
	fetching: false,
	accessToken: '',
	logoutUrl: '',
	resetPasswordInformation: {
		name: '',
		phone: '',
		username: '',
	},
	rememberMeInformation: {
		email: '',
		rememberMe: false,
	},
};

const authSlice = createSlice({
	initialState,
	name: 'auth',
	reducers: {
		/**---- LOGIN START ---- */
		loginRequest: (state, action: PayloadAction<IPayloadLoginRequest>) => {
			state.fetching = true;
		},
		loginSuccess: (state, action: PayloadAction<IPayloadLoginSuccess>) => {
			const { accessToken } = action.payload;
			state.fetching = false;
			state.accessToken = accessToken;
		},
		loginFailure: (state) => {
			state.fetching = false;
		},
		oauthLoginRequest: (
			state,
			action: PayloadAction<IPayloadOAuthLoginRequest>
		) => {
			state.fetching = true;
		},
		deviceLoginRequest: (
			state,
			action: PayloadAction<IPayloadDeviceLoginRequest>
		) => {
			state.fetching = true;
		},
		oauthLoginSuccess: (
			state,
			action: PayloadAction<IPayloadOAuthLoginResponse>
		) => {
			const { accessToken, logoutUrl } = action.payload;
			state.fetching = false;
			state.accessToken = accessToken;
			state.logoutUrl = logoutUrl;
		},
		verifyOtpRequest: (
			state,
			action: PayloadAction<IPayloadVerifyOtpRequest>
		) => {
			state.fetching = true;
		},
		/**---- LOGIN END ---- */

		/**---- LOGOUT START ---- */
		logoutRequest: (
			state,
			action: PayloadAction<IPayloadLogoutRequest | undefined>
		) => {
			state.fetching = true;
		},
		logoutSuccess: (state) => {
			state.accessToken = '';
			state.logoutUrl = '';
			state.fetching = false;
		},
		logoutFailure: (state) => {
			state.fetching = false;
			state.accessToken = '';
		},
		/**---- LOGOUT END ---- */
		resetAuth: (state) => {
			return {
				...initialState,
			};
		},

		renewAccessToken: (state, action: PayloadAction<IPayloadRenewToken>) => {
			const { accessToken } = action.payload;
			state.accessToken = accessToken;
		},

		saveIdInformation: (
			state,
			action: PayloadAction<{ email: string; rememberMe: boolean }>
		) => {
			const { email, rememberMe } = action.payload;
			state.rememberMeInformation = { email, rememberMe };
		},
	},
});

export const AuthSelectors = {
	selectFetching: (state: RootState) => state.auth.fetching,
	selectAccessToken: (state: RootState) => state.auth.accessToken,
	selectLogoutUrl: (state: RootState) => state.auth.logoutUrl,
	selectLogged: (state: RootState) => !!state.auth.accessToken,
	selectResetPasswordInformation: (state: RootState) =>
		state.auth.resetPasswordInformation,
	selectRememberMeInformation: (state: RootState) =>
		state.auth.rememberMeInformation,
};

export const AuthActions = authSlice.actions;
export const AUTH_PERSIST_KEY = 'authPersist';
export const AuthReducer = persistReducer(
	{
		key: AUTH_PERSIST_KEY,
		storage: storage,
		whitelist: ['accessToken', 'logoutUrl', 'rememberMeInformation'],
	},
	authSlice.reducer
);
